import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  runSimulation,
  getLatestSimulation,
  getSimulationHistory,
} from '../services/api';
import type {
  RunSimulationResponse,
  CrawlSimulationResult,
  SimulationHistoryItem,
} from '../types';

/**
 * Phase 3 — F3.1: AI Crawl Simulation hooks.
 * TanStack React Query wrappers for the crawl simulation API.
 */

/** Fetch the latest simulation result for a product. */
export function useLatestSimulation(productId: string) {
  return useQuery<CrawlSimulationResult | null>({
    queryKey: ['simulation', productId],
    queryFn: () => getLatestSimulation(productId),
    enabled: !!productId,
    staleTime: 60_000,
  });
}

/** Fetch simulation history for a product. */
export function useSimulationHistory(productId: string) {
  return useQuery<SimulationHistoryItem[]>({
    queryKey: ['simulation-history', productId],
    queryFn: () => getSimulationHistory(productId),
    enabled: !!productId,
    staleTime: 60_000,
  });
}

/** Run a new crawl simulation. Always runs DEEP (LLM analysis). */
export function useRunSimulation() {
  const queryClient = useQueryClient();

  return useMutation<
    RunSimulationResponse,
    Error,
    { productId: string }
  >({
    mutationFn: ({ productId }) => runSimulation(productId, 'DEEP'),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['simulation', variables.productId],
      });
      queryClient.invalidateQueries({
        queryKey: ['simulation-history', variables.productId],
      });
      // Refresh billing status so "Crawl simulations used" count updates immediately
      queryClient.invalidateQueries({
        queryKey: ['subscription', 'status'],
      });
    },
  });
}
